import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { UploadSimple, ArrowLeft, CheckCircle } from "@phosphor-icons/react";

const NONE = "__none__";

export default function ImportCsvDialog({ open, onOpenChange, bank }) {
  const qc = useQueryClient();
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState({
    delimiter: "auto",
    date_col: "",
    desc_col: "",
    amount_col: "",
    debit_col: "",
    credit_col: "",
    category: "",
    skip_rows: 0,
    mode: "single", // "single" or "split"
  });
  const [header, setHeader] = useState([]);
  const [preview, setPreview] = useState([]);
  const [errors, setErrors] = useState([]);
  const [errorCount, setErrorCount] = useState(0);
  const [total, setTotal] = useState(0);

  const reset = () => {
    setStep(1);
    setFile(null);
    setHeader([]);
    setPreview([]);
    setErrors([]);
    setErrorCount(0);
    setTotal(0);
    setCfg({
      delimiter: "auto",
      date_col: "",
      desc_col: "",
      amount_col: "",
      debit_col: "",
      credit_col: "",
      category: "",
      skip_rows: 0,
      mode: "single",
    });
  };

  const close = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  // Read header only client-side for column mapping
  const onFile = async (f) => {
    setFile(f);
    if (!f) return;
    const text = await f.text();
    const firstLine = text.split(/\r?\n/).find((l) => l.trim()) || "";
    const guess = [";", ",", "\t", "|"].find((d) => firstLine.includes(d)) || ",";
    const cols = firstLine.split(guess).map((c) => c.trim());
    setHeader(cols);
    // Auto-guess common Turkish bank column names
    const find = (patterns) =>
      cols.find((c) =>
        patterns.some((p) => c.toLowerCase().includes(p))
      ) || "";
    setCfg((c) => ({
      ...c,
      delimiter: c.delimiter === "auto" ? "auto" : c.delimiter,
      date_col: find(["tarih", "date", "işlem tarihi"]),
      desc_col: find(["açıklama", "aciklama", "description", "işlem", "detay"]),
      amount_col: find(["tutar", "amount", "işlem tutarı"]),
      debit_col: find(["borç", "borc", "debit", "çıkış", "cikis"]),
      credit_col: find(["alacak", "credit", "giriş", "giris"]),
    }));
    setStep(2);
  };

  const runPreview = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("bank_account_id", bank.id);
      fd.append("currency", bank.currency);
      fd.append("delimiter", cfg.delimiter);
      fd.append("date_col", cfg.date_col);
      if (cfg.desc_col) fd.append("desc_col", cfg.desc_col);
      if (cfg.category) fd.append("category", cfg.category);
      fd.append("skip_rows", String(cfg.skip_rows));
      if (cfg.mode === "single") {
        fd.append("amount_col", cfg.amount_col);
      } else {
        if (cfg.debit_col) fd.append("debit_col", cfg.debit_col);
        if (cfg.credit_col) fd.append("credit_col", cfg.credit_col);
      }
      fd.append("commit", "false");
      const { data } = await api.post("/transactions/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data.preview || []);
      setErrors(data.errors || []);
      setErrorCount(data.error_count || 0);
      setTotal(data.total_rows || 0);
      setStep(3);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Önizleme başarısız");
    } finally {
      setBusy(false);
    }
  };

  const runCommit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("bank_account_id", bank.id);
      fd.append("currency", bank.currency);
      fd.append("delimiter", cfg.delimiter);
      fd.append("date_col", cfg.date_col);
      if (cfg.desc_col) fd.append("desc_col", cfg.desc_col);
      if (cfg.category) fd.append("category", cfg.category);
      fd.append("skip_rows", String(cfg.skip_rows));
      if (cfg.mode === "single") {
        fd.append("amount_col", cfg.amount_col);
      } else {
        if (cfg.debit_col) fd.append("debit_col", cfg.debit_col);
        if (cfg.credit_col) fd.append("credit_col", cfg.credit_col);
      }
      fd.append("commit", "true");
      const { data } = await api.post("/transactions/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.inserted} hareket eklendi (${data.skipped} atlandı)`);
      qc.invalidateQueries();
      close(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaydetme başarısız");
    } finally {
      setBusy(false);
    }
  };

  const ColumnSelect = ({ value, onChange, testid, allowNone = false }) => (
    <Select
      value={value || NONE}
      onValueChange={(v) => onChange(v === NONE ? "" : v)}
    >
      <SelectTrigger data-testid={testid}>
        <SelectValue placeholder="Seç" />
      </SelectTrigger>
      <SelectContent>
        {allowNone && <SelectItem value={NONE}>— Yok —</SelectItem>}
        {header.map((h, i) => (
          <SelectItem key={`${h}-${i}`} value={h}>
            {h}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            CSV İçe Aktar — {bank?.name}{" "}
            <span className="text-xs text-muted-foreground font-normal">
              ({bank?.currency})
            </span>
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4" data-testid="import-step-upload">
            <div className="rounded-md border border-dashed border-border p-8 text-center">
              <UploadSimple size={32} className="mx-auto text-muted-foreground" />
              <div className="mt-3 text-sm text-muted-foreground">
                Banka ekstresi CSV dosyanızı seçin
              </div>
              <Input
                type="file"
                accept=".csv,.txt"
                onChange={(e) => onFile(e.target.files?.[0] || null)}
                className="mt-4 mx-auto max-w-sm"
                data-testid="import-file"
              />
            </div>
            <div className="text-xs text-muted-foreground leading-relaxed">
              • Otomatik ayraç algılama (virgül, noktalı virgül, tab)
              <br />• TR (1.234,56) ve EN (1,234.56) sayı formatları desteklenir
              <br />• Tarih formatları: 01.01.2026, 2026-01-01, 01/01/26 vb.
              <br />• Tutar sütunu tek kolon (işaretli) veya Borç/Alacak ikilisi olabilir
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="import-step-map">
            <div className="text-sm text-muted-foreground">
              Sütunları eşleyin. Dosya başlığı otomatik algılandı.
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Ayraç</Label>
                <Select
                  value={cfg.delimiter}
                  onValueChange={(v) => setCfg({ ...cfg, delimiter: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Otomatik</SelectItem>
                    <SelectItem value=",">Virgül (,)</SelectItem>
                    <SelectItem value=";">Noktalı Virgül (;)</SelectItem>
                    <SelectItem value="\t">Tab</SelectItem>
                    <SelectItem value="|">Boru (|)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Başlık öncesi atlanacak satır</Label>
                <Input
                  type="number"
                  min="0"
                  value={cfg.skip_rows}
                  onChange={(e) =>
                    setCfg({ ...cfg, skip_rows: parseInt(e.target.value || 0) })
                  }
                />
              </div>
              <div>
                <Label>Tarih Sütunu *</Label>
                <ColumnSelect
                  value={cfg.date_col}
                  onChange={(v) => setCfg({ ...cfg, date_col: v })}
                  testid="col-date"
                />
              </div>
              <div>
                <Label>Açıklama Sütunu</Label>
                <ColumnSelect
                  value={cfg.desc_col}
                  onChange={(v) => setCfg({ ...cfg, desc_col: v })}
                  testid="col-desc"
                  allowNone
                />
              </div>
            </div>

            <div>
              <Label>Tutar Modu</Label>
              <div className="mt-1 flex gap-2">
                <button
                  onClick={() => setCfg({ ...cfg, mode: "single" })}
                  className={`flex-1 rounded-md border px-3 py-2 text-sm ${
                    cfg.mode === "single"
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-white"
                  }`}
                  data-testid="mode-single"
                >
                  Tek Sütun (işaretli)
                </button>
                <button
                  onClick={() => setCfg({ ...cfg, mode: "split" })}
                  className={`flex-1 rounded-md border px-3 py-2 text-sm ${
                    cfg.mode === "split"
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-white"
                  }`}
                  data-testid="mode-split"
                >
                  Borç / Alacak
                </button>
              </div>
            </div>

            {cfg.mode === "single" ? (
              <div>
                <Label>Tutar Sütunu *</Label>
                <ColumnSelect
                  value={cfg.amount_col}
                  onChange={(v) => setCfg({ ...cfg, amount_col: v })}
                  testid="col-amount"
                />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Borç (Gider) Sütunu</Label>
                  <ColumnSelect
                    value={cfg.debit_col}
                    onChange={(v) => setCfg({ ...cfg, debit_col: v })}
                    testid="col-debit"
                    allowNone
                  />
                </div>
                <div>
                  <Label>Alacak (Gelir) Sütunu</Label>
                  <ColumnSelect
                    value={cfg.credit_col}
                    onChange={(v) => setCfg({ ...cfg, credit_col: v })}
                    testid="col-credit"
                    allowNone
                  />
                </div>
              </div>
            )}
            <div>
              <Label>Kategori (opsiyonel, tüm satırlara)</Label>
              <Input
                value={cfg.category}
                onChange={(e) => setCfg({ ...cfg, category: e.target.value })}
                placeholder="Banka Ekstresi"
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                data-testid="back-to-upload"
              >
                <ArrowLeft size={14} className="mr-1" /> Geri
              </Button>
              <Button
                disabled={
                  busy ||
                  !cfg.date_col ||
                  (cfg.mode === "single" ? !cfg.amount_col : !cfg.debit_col && !cfg.credit_col)
                }
                onClick={runPreview}
                data-testid="run-preview"
              >
                {busy ? "İşleniyor..." : "Önizle →"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="import-step-preview">
            <div className="flex flex-wrap gap-3 text-sm">
              <div className="rounded-md border border-border bg-white px-3 py-2">
                <div className="overline">Ayraç</div>
                <div className="mono">{cfg.delimiter === "auto" ? "otomatik" : cfg.delimiter}</div>
              </div>
              <div className="rounded-md border border-border bg-white px-3 py-2">
                <div className="overline">Geçerli Satır</div>
                <div className="mono text-emerald-700 font-semibold">{total}</div>
              </div>
              <div className="rounded-md border border-border bg-white px-3 py-2">
                <div className="overline">Hatalı Satır</div>
                <div className="mono text-red-600 font-semibold">{errorCount}</div>
              </div>
            </div>
            <div className="max-h-80 overflow-auto rounded-md border border-border">
              <table className="w-full text-xs" data-testid="preview-table">
                <thead className="border-b border-border bg-secondary/50">
                  <tr className="text-left uppercase tracking-wider text-muted-foreground">
                    <th className="px-3 py-2">Satır</th>
                    <th className="px-3 py-2">Tarih</th>
                    <th className="px-3 py-2">Tür</th>
                    <th className="px-3 py-2">Açıklama</th>
                    <th className="px-3 py-2 text-right">Tutar</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((p) => (
                    <tr key={p.line} className="border-b border-border/60">
                      <td className="px-3 py-1.5 mono text-muted-foreground">{p.line}</td>
                      <td className="px-3 py-1.5 mono">{fmtDate(p.date)}</td>
                      <td className="px-3 py-1.5">
                        <span
                          className={`rounded-sm px-1.5 py-0.5 text-[10px] ${
                            p.kind === "income"
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-red-50 text-red-700"
                          }`}
                        >
                          {p.kind === "income" ? "Gelir" : "Gider"}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-muted-foreground max-w-md truncate">
                        {p.description || "-"}
                      </td>
                      <td
                        className={`px-3 py-1.5 mono text-right ${
                          p.kind === "income" ? "text-emerald-700" : "text-red-600"
                        }`}
                      >
                        {p.kind === "income" ? "+" : "-"}
                        {fmtMoney(p.amount, p.currency)}
                      </td>
                    </tr>
                  ))}
                  {preview.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                        Önizlenecek satır yok
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {errors.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground">
                  {errors.length} hata detayını göster
                </summary>
                <ul className="mt-2 space-y-1">
                  {errors.map((er, i) => (
                    <li key={i} className="text-red-600">
                      Satır {er.line}: {er.reason}
                    </li>
                  ))}
                </ul>
              </details>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(2)} data-testid="back-to-map">
                <ArrowLeft size={14} className="mr-1" /> Sütunları Değiştir
              </Button>
              <Button
                onClick={runCommit}
                disabled={busy || total === 0}
                data-testid="run-commit"
              >
                {busy ? "Kaydediliyor..." : (
                  <>
                    <CheckCircle size={14} className="mr-1" /> {total} Hareketi Kaydet
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
